import React, { Fragment, useCallback, useEffect } from 'react';
import { forceCheck as lazyLoadCheck } from 'react-lazyload';
import { useDispatch, useSelector } from 'react-redux';
import { GroupedVirtuoso, Virtuoso } from 'react-virtuoso';
import { styled } from '@linaria/react';
import { HotelCard } from 'components/HotelCard';
import { Pagination } from 'components/Pagination';
import { PartialMatchBanner } from 'components/PartialMatchBanner/PartialMatchBanner';
import { NoResults } from 'components/SearchUnavailability/NoResults';
import throttle from 'lodash/throttle';
import { trackTimingEvent } from 'middleware/analytics';
import { getIsAcquiringGeoCoordinates, getIsSyntheticTest } from 'modules/meta/selectors';
import { setShowComparisonOffers } from 'modules/meta/slice';
import { getAnchorHotelId, getAnchorType, getGroupedHotelDetails, getHotelHasOffers, getHotelIdsToDisplay, getIsFiltersApplied, getIsSearchCompleted, getIsSearchHasResults, getIsWaitingForHotelsData, getSearchId, getTotalCountOfResults, State } from 'modules/sapiSearch/selectors';
import { AnchorType, Hotel } from 'modules/sapiSearch/slice';
import { getIsHomeSearch } from 'modules/search/selectors';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { GenericError } from '@daedalus/atlas/GenericError';
import { Text } from '@daedalus/atlas/Text';
import { Entity, Team } from '@daedalus/core/src/analytics/types/Events';
import { trackSearchResultsPageFRP, trackSearchResultsPageMRP } from '@daedalus/core/src/analytics/utils/trackPerformanceMetrics';
import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage';
import { SearchResultsListItem } from './SearchResultsListItem';
const LAZY_LOAD_THROTTLE_INTERVAL = 500; // ms
const LOADERS_COUNT = 10;
const HeaderContainer = /*#__PURE__*/styled('div')({
  name: "HeaderContainer",
  class: "h1y5evkz",
  propsAsIs: false
});
const NoResultsWrapper = /*#__PURE__*/styled('div')({
  name: "NoResultsWrapper",
  class: "n1f6cctb",
  propsAsIs: false
});
export const throttledLazyLoadCheck = throttle(() => {
  lazyLoadCheck();
}, LAZY_LOAD_THROTTLE_INTERVAL);
const Loaders: React.FC = () => {
  const loadingList: number[] = new Array(LOADERS_COUNT).fill(1);
  return <>
      {loadingList.map((_, i) => <HotelCard isLoading key={['HotelCardLoader', i].join('_')} />)}
    </>;
};
export const SearchResultsList: React.FC = () => {
  const dispatch = useDispatch();
  const hotelIds = useSelector(getHotelIdsToDisplay);
  const isSearchHasResults = useSelector(getIsSearchHasResults);
  const isWaitingForHotelsData = useSelector(getIsWaitingForHotelsData);
  const isFiltersApplied = useSelector(getIsFiltersApplied);
  const totalResultsCount = useSelector(getTotalCountOfResults);
  const searchIsHotelSearch = useSelector(getAnchorType) === AnchorType.hotel;
  const anchorHotelId = useSelector(getAnchorHotelId);
  const anchorHotelHasOffers = useSelector((state: State) => getHotelHasOffers(state, anchorHotelId));
  const firstHotelHasOffers = useSelector((state: State) => getHotelHasOffers(state, hotelIds[0]));
  const searchId = useSelector(getSearchId);
  const isAcquiringGeoCoordinates = useSelector(getIsAcquiringGeoCoordinates);
  const isSyntheticTest = useSelector(getIsSyntheticTest);
  const isHomeSearch = useSelector(getIsHomeSearch);
  const isSearchCompleted = useSelector(getIsSearchCompleted);
  const {
    isDesktop
  } = useDeviceLayout();
  useEffect(() => {
    trackSearchResultsPageFRP(dispatch);
    if (searchId) {
      trackTimingEvent(dispatch, 'Interaction', 'search-start', isHomeSearch ? Entity.HomePageSearch : Entity.SearchResultsPageSearch, Team.Default, true);
    }
  }, [searchId, isHomeSearch]);
  const isAnchorScenario = Boolean(anchorHotelId);
  useEffect(() => {
    const offerIsVisible = isAnchorScenario ? anchorHotelHasOffers : firstHotelHasOffers;
    if (offerIsVisible || isSearchCompleted) {
      trackSearchResultsPageMRP(dispatch);
    }
  }, [isAnchorScenario, dispatch, anchorHotelHasOffers, firstHotelHasOffers, isSearchCompleted]);

  // eslint-disable-next-line no-warning-comments
  // TODO (@Search): Decide whether to keep or fix this bug
  // Emulating bug from the A side of SAPI A/B test by not showing messages for hotel searches.
  // It's up to Search sqd to decide what should be the correct behaviour
  const hasNoAvailableResults = !isSearchHasResults && !searchIsHotelSearch;
  const shouldShowNoResultsMessage = totalResultsCount === 0 || isFiltersApplied && hasNoAvailableResults;
  const {
    hasPartiallyMatched,
    greatDeals,
    groupCounts
  } = useSelector(getGroupedHotelDetails);
  const shouldShowPartialMatchBanner = hasPartiallyMatched && greatDeals.length <= 10;
  useEffect(() => {
    dispatch(setShowComparisonOffers({
      showComparisonOffers: true
    }));
  }, [dispatch]);
  const renderHotelItem = useCallback((index: number) => {
    const hotelId: Hotel['objectID'] = hotelIds[index];
    return <SearchResultsListItem index={isAnchorScenario ? index + 1 : index} hotelId={hotelId} />;
  }, [hotelIds, isAnchorScenario]);
  const renderGroupHeader = (index: number): React.ReactNode => {
    // Hide the great deal header when we don't have any great Deals
    if (greatDeals.length === 0 && index === 0) return null;
    // Hide the great deal header when we don't have anchor hotel
    if (!isAnchorScenario && index === 0) return null;
    return <>
        {index === 1 && shouldShowPartialMatchBanner && <PartialMatchBanner numResults={greatDeals.length} />}
        {isDesktop && index === 1 && <HeaderContainer>
            <Text as="h1" variant="titleL">
              <FormattedMessageWrapper id="partialResultsHeading" defaultMessage="Results that match some of your criteria" />
            </Text>
          </HeaderContainer>}
      </>;
  };
  if (isAcquiringGeoCoordinates || isWaitingForHotelsData) return <Loaders />;
  if (shouldShowNoResultsMessage || hasNoAvailableResults) return <NoResultsWrapper>
        <GenericError>
          <NoResults />
        </GenericError>
      </NoResultsWrapper>;
  if (hasPartiallyMatched) {
    return <Pagination>
        <GroupedVirtuoso useWindowScroll groupCounts={groupCounts} increaseViewportBy={300} components={{
        TopItemList: Fragment
      }} groupContent={groupIndex => renderGroupHeader(groupIndex)} itemContent={index => renderHotelItem(index)} />
      </Pagination>;
  }
  return <Pagination>
      {isSyntheticTest ? hotelIds.map((_, index) => renderHotelItem(index)) : <Virtuoso
    // initialItemCount prevents the flash of white space when the cards load
    initialItemCount={Math.min(3, hotelIds.length)} style={{
      minHeight: '100vh'
    }} useWindowScroll data={hotelIds} increaseViewportBy={300} itemContent={index => renderHotelItem(index)} />}
    </Pagination>;
};

require("./SearchResultsList.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SearchResultsList.tsx");